import { useState, useCallback, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import type { Node as ReactFlowNode } from 'reactflow'
import { ChevronLeft, ChevronsLeft, ChevronsRight, Pencil, Settings, Sparkles, Terminal } from 'lucide-react'
import { AgentChat } from './AgentChat'
import { useUpdateProject } from '../../api/hooks'
import { cn } from '../../lib/utils'
import {
  WorkspaceSidebar,
  SidebarHeader,
  SidebarContent,
  ResizeHandle,
} from './WorkspaceShell'

interface ChatSidebarProps {
  projectId: string
  projectName?: string
  initialPrompt?: string
  style?: string
  productionTarget?: string
  onStop?: () => void
  skipCostConfirmation?: boolean
  onSkipCostChange?: (value: boolean) => void
  /** Canvas nodes for @mention autocomplete */
  canvasNodes?: ReactFlowNode[]
  /** Running non-director node count from YDoc (includes canvas-hidden nodes) */
  ydocRunningCount?: number
  /** Queued (pending_execution) non-director node count from YDoc */
  ydocQueuedCount?: number
  /** Called when a mention pill is clicked — focuses the canvas on that node */
  onMentionClick?: (nodeId: string) => void
  /** Callback when user selects a file to upload from Director chat */
  onUploadFile?: (file: File, type: 'image' | 'video' | 'audio') => void
}

/**
 * Collapsible and resizable chat sidebar for the workspace.
 * Contains the AgentChat component for agentic workflow interactions.
 */
export function ChatSidebar({
  projectId,
  projectName,
  initialPrompt,
  style,
  productionTarget,
  onStop,
  skipCostConfirmation = true,
  onSkipCostChange,
  canvasNodes,
  ydocRunningCount,
  ydocQueuedCount,
  onMentionClick,
  onUploadFile,
}: ChatSidebarProps) {
  const [chatCollapsed, setChatCollapsed] = useState(() => {
    try { return localStorage.getItem('chat-sidebar-collapsed') === 'true' } catch { return false }
  })
  const [chatWidth, setChatWidth] = useState(380)
  const [showSettings, setShowSettings] = useState(false)
  const [isEditingName, setIsEditingName] = useState(false)
  const [editingName, setEditingName] = useState('')
  const editInputRef = useRef<HTMLInputElement>(null)
  const settingsRef = useRef<HTMLDivElement>(null)
  const isCancellingRef = useRef(false)
  const updateProject = useUpdateProject()
  const isResizingRef = useRef(false)
  const startXRef = useRef(0)
  const startWidthRef = useRef(0)

  useEffect(() => {
    if (isEditingName && editInputRef.current) {
      editInputRef.current.focus()
      editInputRef.current.select()
    }
  }, [isEditingName])

  // Open chat sidebar when a node's "Add to Chat" toolbar button is clicked
  useEffect(() => {
    const handleAddNodeToChat = () => {
      setChatCollapsed(false)
    }
    window.addEventListener('add-node-to-chat', handleAddNodeToChat)
    return () => window.removeEventListener('add-node-to-chat', handleAddNodeToChat)
  }, [])

  // Persist collapsed state to localStorage
  useEffect(() => {
    try { localStorage.setItem('chat-sidebar-collapsed', String(chatCollapsed)) } catch { /* ignore */ }
  }, [chatCollapsed])

  useEffect(() => {
    if (!showSettings) return
    const handleClickOutside = (e: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        setShowSettings(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showSettings])

  const handleStartEditing = useCallback(() => {
    setEditingName(projectName || '')
    setIsEditingName(true)
  }, [projectName])

  const handleSaveName = useCallback(() => {
    if (isCancellingRef.current) {
      isCancellingRef.current = false
      return
    }
    const trimmed = editingName.trim()
    if (trimmed && trimmed !== projectName) {
      updateProject.mutate({ projectId, data: { name: trimmed } })
    }
    setIsEditingName(false)
  }, [editingName, projectName, projectId, updateProject])

  const handleCancelEditing = useCallback(() => {
    isCancellingRef.current = true
    setIsEditingName(false)
    setEditingName('')
  }, [])

  const handleEditKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSaveName()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancelEditing()
    }
  }, [handleSaveName, handleCancelEditing])

  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    isResizingRef.current = true
    startXRef.current = e.clientX
    startWidthRef.current = chatWidth
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }, [chatWidth])

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return
      const delta = e.clientX - startXRef.current
      const newWidth = Math.min(Math.max(startWidthRef.current + delta, 280), 800)
      setChatWidth(newWidth)
    }

    const handleMouseUp = () => {
      if (isResizingRef.current) {
        isResizingRef.current = false
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])

  return (
    <WorkspaceSidebar collapsed={chatCollapsed} width={chatWidth}>
      {/* Resize handle */}
      {!chatCollapsed && <ResizeHandle onMouseDown={handleResizeMouseDown} />}

      {/* Sidebar Header */}
      <SidebarHeader>
        {!chatCollapsed && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 min-w-0 flex-1 mr-2">
                <Link
                  to="/dashboard"
                  className="group text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                  title="Back to dashboard"
                >
                  <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                </Link>
                {isEditingName ? (
                  <input
                    ref={editInputRef}
                    data-testid="project-name-input"
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onBlur={handleSaveName}
                    onKeyDown={handleEditKeyDown}
                    className={cn(
                      'text-sm text-foreground bg-surface-2 border border-border rounded px-1.5 py-0.5',
                      'outline-none focus:border-primary/50 min-w-0 flex-1 truncate'
                    )}
                  />
                ) : (
                  <>
                    <span className="text-sm text-muted-foreground truncate min-w-0" data-testid="project-name-display">
                      {projectName || 'Untitled'}
                    </span>
                    <button
                      data-testid="edit-project-name-button"
                      onClick={handleStartEditing}
                      className="p-1 rounded hover:bg-surface-2 transition-colors text-muted-foreground hover:text-foreground flex-shrink-0 cursor-pointer"
                      title="Edit project name"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="text-xxs font-medium text-muted-foreground flex items-center gap-1.5 bg-muted px-2 py-1 rounded">
                  <Sparkles className="w-3 h-3 text-primary" />
                  Director
                </div>
                {import.meta.env.DEV && import.meta.env.VITE_DIRECTOR_PROVIDER === 'claude-code' && (
                  <div className="text-xxs font-medium text-emerald-400 flex items-center gap-1 bg-emerald-400/10 px-2 py-1 rounded" data-testid="claude-code-badge">
                    <Terminal className="w-3 h-3" />
                    Claude Code
                  </div>
                )}
                <div className="relative" ref={settingsRef}>
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    className={cn(
                      "p-1.5 rounded-lg hover:bg-surface-2 transition-colors text-muted-foreground hover:text-foreground cursor-pointer",
                      showSettings && "bg-surface-2 text-foreground"
                    )}
                    title="Director settings"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                  {showSettings && (
                    <div className="absolute right-0 top-full mt-2 z-50">
                      <div className="w-52 bg-surface-light/95 backdrop-blur-sm border border-white/10 rounded-lg shadow-xl p-3 animate-scale-in">
                        <div className="text-xs text-text-secondary mb-3">Director Settings</div>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer hover:text-foreground transition-colors">
                            <input
                              type="checkbox"
                              checked={skipCostConfirmation}
                              onChange={(e) => onSkipCostChange?.(e.target.checked)}
                              className="w-3.5 h-3.5 rounded border-white/30 bg-transparent text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer accent-primary"
                            />
                            Auto confirm costs
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setChatCollapsed(!chatCollapsed)}
                  className="p-1.5 rounded-lg hover:bg-surface-2 transition-colors text-muted-foreground hover:text-foreground cursor-pointer"
                  title="Collapse chat"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Collapsed state header */}
        {chatCollapsed && (
          <button
            onClick={() => setChatCollapsed(false)}
            className="p-1.5 rounded-lg hover:bg-surface-2 transition-colors text-muted-foreground hover:text-foreground mx-auto block cursor-pointer"
            title="Expand chat"
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        )}
      </SidebarHeader>

      {/* Chat Content */}
      <SidebarContent>
        {!chatCollapsed && (
          <AgentChat
            projectId={projectId}
            initialPrompt={initialPrompt}
            style={style}
            productionTarget={productionTarget}
            className="flex-1 overflow-hidden h-full"
            onStop={onStop}
            skipCostConfirmation={skipCostConfirmation}
            canvasNodes={canvasNodes}
            ydocRunningCount={ydocRunningCount}
            ydocQueuedCount={ydocQueuedCount}
            onMentionClick={onMentionClick}
            onUploadFile={onUploadFile}
          />
        )}
      </SidebarContent>

      {/* Collapsed state - show icon to expand */}
      {chatCollapsed && (
        <div className="flex-1 flex flex-col items-center pt-6 gap-4">
          <button
            onClick={() => setChatCollapsed(false)}
            className="p-2.5 rounded-md bg-surface-1 hover:bg-surface-2 border border-border hover:border-white/10 transition-all text-muted-foreground hover:text-foreground cursor-pointer"
            title="Open Director chat"
          >
            <Sparkles className="w-5 h-5 text-primary" />
          </button>
        </div>
      )}

    </WorkspaceSidebar>
  )
}
